import {
  type ChangeEventHandler,
  useState,
  type FC,
  useMemo,
  useCallback,
} from 'react';
import { useSession } from 'next-auth/react';
import {
  Button,
  Flex,
  FormControl,
  Input,
  Text,
} from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { Drawer } from '@/components/modules/Drawer';
import { useUpdateAccount } from '@/modules/Customer/http/client/mutations/useUpdateAccount';
import { useUpdateEmail } from '@/modules/Customer/http/client/mutations/useUpdateEmail';
import { useUpdatePhone } from '@/modules/Customer/http/client/mutations/useUpdatePhone';
import { useVerifyEmail } from '@/modules/Customer/http/client/mutations/useVerifyEmail';
import { useVerifyPhone } from '@/modules/Customer/http/client/mutations/useVerifyPhone';
import { validatePhone } from '@/modules/Customer/http/client/validatePhone';
import { EDIT_PROFILE_INFO_DRAWER_IDS } from '../utils';
import { sanitizeNumber } from '@/utils/format';
import { formatPhoneNumber } from '@/utils/masks';
import { checkIsEmail } from '@/utils/string';
import { OTPConfirmation } from './OTPConfirmation';
import { SuccessMessage } from './SuccessMessage';

export const EditProfileInfoDrawer: FC<EditProfileInfoDrawerProps> = ({
  currentValue,
  type,
}) => {
  const t = useT();
  const { update: updateSession } = useSession();

  const { mutate: updateName, isPending: isUpdatingName } = useUpdateAccount();
  const { mutate: updateEmail, isPending: isUpdatingEmail } = useUpdateEmail();
  const {
    mutate: updatePhone,
    isPending: isUpdatingPhone,
    isError: isUpdatingPhoneError,
  } = useUpdatePhone();
  const {
    mutate: verifyEmail,
    isPending: isVerifyingEmail,
    isError: isVerifyingEmailError,
  } = useVerifyEmail();
  const {
    mutate: verifyPhone,
    isPending: isVerifyingPhone,
    isError: isVerifyingPhoneError,
  } = useVerifyPhone();

  const [value, setValue] = useState('');
  const [isNewValueSet, setIsNewValueSet] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const isPending =
    isUpdatingName ||
    isUpdatingEmail ||
    isUpdatingPhone ||
    isVerifyingEmail ||
    isVerifyingPhone;

  const isError =
    isUpdatingPhoneError || isVerifyingEmailError || isVerifyingPhoneError;

  const sendOTPByType = useMemo(
    () => ({
      phone: () =>
        updatePhone(
          { phone: sanitizeNumber(value) },
          { onSuccess: () => updateSession({ phone: sanitizeNumber(value) }) }
        ),
      email: () =>
        updateEmail(
          { email: value },
          { onSuccess: () => updateSession({ email: sanitizeNumber(value) }) }
        ),
    }),
    [updateEmail, updatePhone, updateSession, value]
  );

  const changeInputValue: ChangeEventHandler<HTMLInputElement> = ({ target }) =>
    setValue(type === 'phone' ? formatPhoneNumber(target.value) : target.value);

  const validateInput = useCallback(
    async ({ value, onSuccess }: { value: string; onSuccess: () => void }) => {
      setIsValidating(true);
      const isValid = await validateByType[type](
        type === 'phone' ? sanitizeNumber(value) : value
      );
      setIsValidating(false);
      if (isValid) onSuccess();
    },
    [type]
  );

  const saveProfileChanges = async (value: string, token: string) => {
    if (type === 'phone') {
      verifyPhone(
        { phone: sanitizeNumber(value), token },
        { onSuccess: () => setIsSuccess(true) }
      );
    }
    verifyEmail(
      { email: value, token },
      { onSuccess: () => setIsSuccess(true) }
    );
  };

  const handleNameUpdate = useCallback(
    () =>
      updateName(
        { name: value },
        {
          onSuccess: () => {
            setIsSuccess(true);
            updateSession({ name: value });
          },
        }
      ),
    [updateName, updateSession, value]
  );

  const resetProfileEdition = () => {
    if (isNewValueSet) {
      return;
    }

    setValue('');
    setIsNewValueSet(false);
    setIsSuccess(false);
  };

  const button = useMemo(() => {
    if (type === 'name') {
      return {
        content: t('drawers.personalInfo.buttons.save'),
        action: (value: string) =>
          validateInput({
            onSuccess: handleNameUpdate,
            value,
          }),
      };
    }

    return {
      content: t('drawers.personalInfo.buttons.next'),
      action: (value: string) =>
        validateInput({
          onSuccess: () => {
            setIsNewValueSet(true);
            sendOTPByType[type as RequiredOTPFlows]();
          },
          value,
        }),
    };
  }, [handleNameUpdate, sendOTPByType, t, type, validateInput]);

  const onClose = () => {
    window.dispatchEvent(
      new CustomEvent(
        `close-chakra-modal-${EDIT_PROFILE_INFO_DRAWER_IDS[type]}`
      )
    );
  };

  return (
    <Drawer
      id={EDIT_PROFILE_INFO_DRAWER_IDS[type]}
      headerContent={
        isSuccess ? undefined : (
          <Text size="2xl" color="content.primary">
            {t(`drawers.personalInfo.${type}.header`)}
          </Text>
        )
      }
      drawerProps={{ closeOnEsc: !isSuccess, closeOnOverlayClick: !isSuccess }}
      showCloseButton={!isSuccess}
      closeButtonProps={{ top: '3.125rem' }}
      headerProps={{
        pl: '3.4375rem',
        pr: '3rem',
        py: '2.8125rem',
      }}
      bodyProps={{ px: '2xl' }}
      handleBeforeDrawerClose={resetProfileEdition}
    >
      <Flex as="form" flexDir="column" h="full" justify="space-between">
        {isSuccess && <SuccessMessage resetFlow={resetProfileEdition} />}
        {!isSuccess && (
          <OTPConfirmation
            type={type}
            isNewValueSet={isNewValueSet}
            isLoading={isPending}
            isError={isError}
            resendOTP={() => sendOTPByType[type as RequiredOTPFlows]()}
            handleOTPValidation={(token) => saveProfileChanges(value, token)}
          >
            <Flex as="form" flexDir="column" gap="xl">
              <FormControl
                label={t(`drawers.personalInfo.${type}.registered`)}
                isDisabled
              >
                <Input value={currentValue} />
              </FormControl>

              <FormControl
                label={t(`drawers.personalInfo.${type}.newValueLabel`)}
                id={type}
              >
                <Input
                  autoFocus
                  name={type}
                  value={value}
                  onChange={changeInputValue}
                  onKeyUpCapture={(e) => {
                    if (e.code === 'Enter') {
                      button.action(value);
                    }
                  }}
                />
              </FormControl>
              <Flex gap="m" flexDir="column">
                <Button
                  isDisabled={value.length === 0}
                  onClick={() => button.action(value)}
                  isLoading={isValidating || isPending}
                  accessKey="Enter"
                >
                  {button.content}
                </Button>
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
              </Flex>
            </Flex>
          </OTPConfirmation>
        )}
      </Flex>
    </Drawer>
  );
};

const validateByType = {
  email: async (value: string) => checkIsEmail(value),
  name: async (value: string) => value.length >= 2,
  phone: async (value: string) => {
    const { isPhoneValid } = await validatePhone(value);
    return isPhoneValid;
  },
};

export type EditProfileInfoDrawerProps = {
  currentValue: string;
  type: Exclude<keyof typeof EDIT_PROFILE_INFO_DRAWER_IDS, 'password'>;
};

type RequiredOTPFlows = Exclude<EditProfileInfoDrawerProps['type'], 'name'>;
